import { useState, useEffect } from 'react';
import { useAdminAuth } from './AdminAuthContext';
import { useAdminTheme } from './AdminThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import {
  Key,
  Plus,
  Loader2,
  RefreshCw,
  Copy,
  Trash2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Shield,
  ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const WebhookKeys = () => {
  const { isSuperAdmin } = useAdminAuth();
  const { isDarkMode } = useAdminTheme();
  const [keys, setKeys] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newKeyModalOpen, setNewKeyModalOpen] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [keyName, setKeyName] = useState('');
  const [keyDescription, setKeyDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [showLogs, setShowLogs] = useState(false);

  useEffect(() => {
    if (isSuperAdmin()) {
      fetchKeys();
      fetchLogs();
    }
  }, []);

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/webhook-keys`, { withCredentials: true });
      setKeys(response.data);
    } catch (error) {
      console.error('Error fetching webhook keys:', error);
      toast.error('Failed to load webhook keys');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/webhook-logs?limit=50`, { withCredentials: true });
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching webhook logs:', error);
    }
  };

  const createKey = async () => {
    if (!keyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/admin/webhook-keys`, {
        name: keyName.trim(),
        description: keyDescription.trim() || null
      }, { withCredentials: true });
      
      setNewApiKey(response.data.api_key);
      setCreateModalOpen(false);
      setNewKeyModalOpen(true);
      setKeyName('');
      setKeyDescription('');
      fetchKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const revokeKey = async (keyId) => {
    setRevoking(keyId);
    try {
      await axios.delete(`${API}/admin/webhook-keys/${keyId}`, { withCredentials: true });
      toast.success('API key revoked');
      fetchKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke API key');
    } finally {
      setRevoking(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isSuperAdmin()) {
    return (
      <div className={`text-center py-12 rounded-xl border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`} data-testid="webhook-restricted">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Access Restricted</h3>
        <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Only Super Admins can manage webhook API keys</p>
      </div>
    );
  }

  return (
    <div data-testid="webhook-keys-page" className="animate-fadeIn">
      <Toaster richColors position="top-right" />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Webhook API Keys</h1>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Manage API keys for external GSM gateways</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className={`transition-all duration-200 active:scale-95 ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            onClick={() => setShowLogs(!showLogs)}
          >
            {showLogs ? 'Hide Logs' : 'View Logs'}
          </Button>
          <Button
            onClick={() => setCreateModalOpen(true)}
            className="bg-teal-600 hover:bg-teal-700 text-white transition-all duration-200 active:scale-95"
            data-testid="create-key-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create API Key
          </Button>
        </div>
      </div>

      {/* Webhook Endpoint Info */}
      <div className={`rounded-xl border p-6 mb-6 shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
            <ExternalLink className="w-6 h-6 text-teal-600" />
          </div>
          <div className="flex-1">
            <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Webhook Endpoint</h3>
            <div className={`rounded-lg p-4 font-mono text-sm ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
              <p className="text-teal-600 mb-2">POST {BACKEND_URL}/api/sms/incoming</p>
              <p className={`text-xs mb-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Include API key in headers:</p>
              <p className={isDarkMode ? 'text-slate-200' : 'text-gray-700'}>X-API-Key: your_api_key</p>
              <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>or</p>
              <p className={isDarkMode ? 'text-slate-200' : 'text-gray-700'}>Authorization: Bearer your_api_key</p>
            </div>
            <div className={`mt-4 rounded-lg p-4 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
              <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>Expected Payload:</p>
              <pre className={`text-xs overflow-x-auto ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>{`{
  "phone_number": "+231XXXXXXXX",
  "network": "MTN" or "Orange",
  "message": "Raw SMS text message",
  "received_at": "2024-01-01T12:00:00Z" (optional)
}`}</pre>
            </div>
          </div>
        </div>
      </div>

      {/* API Keys List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      ) : keys.length === 0 ? (
        <div className={`text-center py-12 rounded-xl border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <Key className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
          <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>No API Keys</h3>
          <p className={`mb-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Create an API key to enable webhook integration</p>
          <Button
            onClick={() => setCreateModalOpen(true)}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create First Key
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {keys.map((key, idx) => (
            <div
              key={key.key_id}
              className={`rounded-xl border p-5 shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 ${
                key.is_active 
                  ? isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-teal-600' : 'bg-white border-gray-200 hover:border-teal-200'
                  : isDarkMode ? 'bg-slate-800 border-red-800 opacity-60' : 'bg-white border-red-200 opacity-60'
              }`}
              style={{ animationDelay: `${idx * 50}ms` }}
              data-testid={`key-card-${key.key_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    key.is_active ? 'bg-emerald-50' : 'bg-red-50'
                  }`}>
                    <Key className={`w-5 h-5 ${key.is_active ? 'text-emerald-600' : 'text-red-500'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{key.name}</h3>
                      {key.is_active ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">
                          <CheckCircle2 className="w-3 h-3" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-red-50 text-red-600">
                          <XCircle className="w-3 h-3" />
                          Revoked
                        </span>
                      )}
                    </div>
                    {key.description && (
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{key.description}</p>
                    )}
                    <div className={`flex items-center gap-3 mt-2 text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                      <span>Key: {key.api_key_masked}</span>
                      <span>•</span>
                      <span>Created: {formatDate(key.created_at)}</span>
                      {key.last_used_at && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Last used: {formatDate(key.last_used_at)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {key.is_active && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => revokeKey(key.key_id)}
                    disabled={revoking === key.key_id}
                    data-testid={`revoke-key-${key.key_id}`}
                  >
                    {revoking === key.key_id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Webhook Logs */}
      {showLogs && (
        <div className="mt-8">
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recent Webhook Requests</h2>
          {logs.length === 0 ? (
            <div className={`text-center py-8 rounded-xl border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>No webhook requests yet</p>
            </div>
          ) : (
            <div className={`rounded-xl border overflow-hidden shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`border-b ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                    <tr>
                      <th className={`text-left text-sm font-medium px-4 py-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Time</th>
                      <th className={`text-left text-sm font-medium px-4 py-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Status</th>
                      <th className={`text-left text-sm font-medium px-4 py-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>API Key</th>
                      <th className={`text-left text-sm font-medium px-4 py-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Client IP</th>
                      <th className={`text-left text-sm font-medium px-4 py-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>SMS ID</th>
                      <th className={`text-left text-sm font-medium px-4 py-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Error</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                    {logs.map((log) => (
                      <tr key={log.log_id} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}>
                        <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>{formatDate(log.created_at)}</td>
                        <td className="px-4 py-3">
                          {log.success ? (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">
                              <CheckCircle2 className="w-3 h-3" />
                              Success
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-red-50 text-red-600">
                              <XCircle className="w-3 h-3" />
                              Failed
                            </span>
                          )}
                        </td>
                        <td className={`px-4 py-3 text-sm font-mono ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>{log.api_key_id}</td>
                        <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>{log.client_ip}</td>
                        <td className={`px-4 py-3 text-sm font-mono ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>{log.sms_id || '-'}</td>
                        <td className="px-4 py-3 text-red-500 text-sm truncate max-w-xs">{log.error_message || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Key Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className={isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-900'}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : 'text-gray-900'}>Create API Key</DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>
              Create a new API key for webhook integration
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className={`text-sm mb-2 block ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Name *</label>
              <Input
                placeholder="e.g., Production GSM Gateway"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}
                data-testid="key-name-input"
              />
            </div>
            <div>
              <label className={`text-sm mb-2 block ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Description (optional)</label>
              <Textarea
                placeholder="What is this API key used for?"
                value={keyDescription}
                onChange={(e) => setKeyDescription(e.target.value)}
                className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}
                data-testid="key-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateModalOpen(false)} className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>
              Cancel
            </Button>
            <Button
              onClick={createKey}
              disabled={creating || !keyName.trim()}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="confirm-create-key-btn"
            >
              {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Create Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Key Display Modal */}
      <Dialog open={newKeyModalOpen} onOpenChange={setNewKeyModalOpen}>
        <DialogContent className={isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-900'}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              API Key Created
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-amber-800 text-sm">
                  <strong>Important:</strong> Copy this API key now. You won't be able to see it again!
                </p>
              </div>
            </div>
            <div className={`rounded-lg p-4 flex items-center justify-between gap-4 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
              <code className="text-teal-600 text-sm break-all">{newApiKey}</code>
              <Button
                size="sm"
                variant="ghost"
                className={`flex-shrink-0 ${isDarkMode ? 'text-slate-400 hover:text-teal-400' : 'text-gray-500 hover:text-teal-600'}`}
                onClick={() => copyToClipboard(newApiKey)}
                data-testid="copy-key-btn"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setNewKeyModalOpen(false)}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WebhookKeys;
